import { useState, useEffect, useRef } from "react";
import { ebayAPI, prefsAPI } from "@/lib/api";
import api from "@/lib/api";
import { Spinner, X, ArrowRight, Warning, MagnifyingGlass, Star, Trash, Camera, Upload, PencilSimple } from "@phosphor-icons/react";
import Editor from "react-simple-wysiwyg";
import CorrectListingModal from "@/components/app/CorrectListingModal";

const RichEditor = Editor.default || Editor;

export default function EbayListingModal({ verdict, onClose, onSuccess, onFailure, editMode = false }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [errorExplanation, setErrorExplanation] = useState(null); // {summary, what_to_fix, fault, ebay_error_id}
  const [explainingError, setExplainingError] = useState(false);
  const [formData, setFormData] = useState(null);
  const [aspects, setAspects] = useState({});
  const [conditionEnum, setConditionEnum] = useState("");
  const [validConditions, setValidConditions] = useState([]);
  const [allowOffers, setAllowOffers] = useState(false);
  const [autoAccept, setAutoAccept] = useState("");
  const [minOffer, setMinOffer] = useState("");
  const [offerPcts, setOfferPcts] = useState({ auto: 30, min: 40 });
  const [refreshingAspects, setRefreshingAspects] = useState(false);
  const [listingPhotos, setListingPhotos] = useState(verdict.photos || []);
  const [policies, setPolicies] = useState(null);
  const [selectedPolicies, setSelectedPolicies] = useState({
    fulfillment: "", payment: "", return: "",
  });
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [stickerPhotoPaths, setStickerPhotoPaths] = useState([]);
  const [labelAutoDetected, setLabelAutoDetected] = useState(false);
  const [showCorrect, setShowCorrect] = useState(false);

  const [policiesError, setPoliciesError] = useState("");

  useEffect(() => {
    loadFormData();
    ebayAPI.policies()
      .then(({ data }) => {
        setPolicies(data);
        // Empty policy lists also indicate a likely auth issue — the API
        // returns {} for unauthenticated/expired token requests rather than
        // throwing in production behind Cloudflare.
        const total = (data?.fulfillment?.length || 0) + (data?.payment?.length || 0) + (data?.return?.length || 0);
        if (total === 0) {
          setPoliciesError("eBay returned no business policies. Your session may have expired — reconnect on Profile.");
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || "";
        if (err?.response?.status === 400 && /not connected|session|expired|reconnect/i.test(detail)) {
          setPoliciesError("Your eBay session expired. Reconnect on Profile before publishing.");
        } else {
          setPoliciesError("Could not load eBay policies. Reconnect on Profile or try again.");
        }
      });
    ebayAPI.campaigns().then(({ data }) => {
      setCampaigns(data.campaigns || []);
      setSelectedCampaign(data.default_campaign_id || "");
    }).catch(() => {});
  }, []);

  const loadFormData = async () => {
    try {
      const [{ data }, prefsRes] = await Promise.all([
        ebayAPI.listingForm(verdict.id, editMode),
        prefsAPI.get().catch(() => ({ data: {} })),
      ]);
      setFormData(data);
      setCustomLabel(data.custom_label || "");
      setLabelAutoDetected(!!(data.custom_label && (data.sticker_photo_paths || []).length > 0));
      setStickerPhotoPaths(data.sticker_photo_paths || []);
      // Load user's offer percentages
      const prefs = prefsRes.data || {};
      const autoPct = prefs.offer_auto_accept_pct ?? 30;
      const minPct = prefs.offer_min_pct ?? 40;
      setOfferPcts({ auto: autoPct, min: minPct });
      // Use verdict photos (user-ordered) over form data photos, minus any
      // sticker paths — those are displayed separately and excluded from eBay.
      const stickerSet = new Set(data.sticker_photo_paths || []);
      const filterStickers = (list) => (list || []).filter((p) => !stickerSet.has(p));
      if (!listingPhotos.length && data.photos?.length) {
        setListingPhotos(filterStickers(data.photos));
      } else {
        setListingPhotos((prev) => filterStickers(prev));
      }
      const initial = { ...(data.prefilled || {}) };
      (data.aspects || []).forEach((a) => {
        if (!(a.name in initial)) initial[a.name] = "";
      });
      setAspects(initial);
      // Set condition dropdown
      if (data.valid_conditions?.length) setValidConditions(data.valid_conditions);
      if (data.condition_enum) setConditionEnum(data.condition_enum);
      // Set offer settings
      if (data.offer_settings) {
        setAllowOffers(data.offer_settings.allow_offers ?? false);
        setAutoAccept(data.offer_settings.auto_accept ?? "");
        setMinOffer(data.offer_settings.min_offer ?? "");
      }
    } catch {
      setError("Failed to load listing form data");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = async (categoryId, categoryName) => {
    if (!formData || categoryId === formData.category_id) return;
    setFormData((prev) => ({ ...prev, category_id: categoryId, category_name: categoryName }));
    setRefreshingAspects(true);
    try {
      const { data } = await ebayAPI.aspects(
        categoryId,
        formData.title,
        aspects.Brand || "",
        formData.condition,
      );
      setFormData((prev) => ({ ...prev, aspects: data.aspects || [] }));
      const merged = { ...aspects };
      (data.aspects || []).forEach((a) => {
        if (!(a.name in merged)) merged[a.name] = "";
      });
      for (const [k, v] of Object.entries(data.prefilled || {})) {
        if (!merged[k]) merged[k] = v;
      }
      setAspects(merged);
      // Refresh conditions for the new category
      if (data.valid_conditions?.length) setValidConditions(data.valid_conditions);
      if (data.condition_enum) setConditionEnum(data.condition_enum);
    } catch {
      // keep existing aspects
    } finally {
      setRefreshingAspects(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData) return;
    if (!editMode) {
      // Custom Label is required so users don't accidentally publish with an
      // empty/auto-generated SKU. Backend re-enforces this.
      if (!customLabel.trim()) {
        setError("Custom Label is required. Add one at the top of the form before publishing.");
        return;
      }
      const missing = (formData.aspects || [])
        .filter((a) => a.required && !aspects[a.name])
        .map((a) => a.name);
      if (missing.length > 0) {
        setError(`Missing required fields: ${missing.join(", ")}`);
        return;
      }
    }
    setSubmitting(true);
    setError("");
    setErrorExplanation(null);
    try {
      let data;
      if (editMode) {
        const resp = await ebayAPI.update({
          verdict_id: verdict.id,
          title: formData.title,
          price: parseFloat(formData.price) || 0,
          description: formData.description,
          condition_enum: conditionEnum,
          aspects,
          photo_paths: listingPhotos,
          allow_offers: allowOffers,
          auto_accept: parseFloat(autoAccept) || 0,
          min_offer: parseFloat(minOffer) || 0,
        });
        data = resp.data;
      } else {
        const resp = await ebayAPI.list({
          verdict_id: verdict.id,
          title: formData.title,
          price: parseFloat(formData.price) || 0,
          description: formData.description,
          condition_enum: conditionEnum,
          aspects,
          photo_paths: listingPhotos,
          fulfillment_policy_id: selectedPolicies.fulfillment,
          payment_policy_id: selectedPolicies.payment,
          return_policy_id: selectedPolicies.return,
          allow_offers: allowOffers,
          auto_accept: parseFloat(autoAccept) || 0,
          min_offer: parseFloat(minOffer) || 0,
          custom_label: customLabel.trim(),
        });
        data = resp.data;
      }
      if (data.success) {
        // Add to campaign if selected (after listing is published).
        // Skip in edit mode — the listing is already promoted on its existing campaign.
        const listingId = data.listing_id || verdict.ebay_listing_id;
        if (!editMode && selectedCampaign && listingId) {
          try {
            // eBay needs a moment to index the listing before it can be promoted
            await new Promise((r) => setTimeout(r, 2000));
            await ebayAPI.addToCampaign(selectedCampaign, listingId);
          } catch (campErr) {
            const campMsg = campErr.response?.data?.detail || "Could not add to campaign";
            setError(`Listed successfully but campaign promotion failed: ${campMsg}`);
            setTimeout(() => onSuccess(data), 4000);
            return;
          }
        }
        // Show warnings (e.g., price couldn't update due to active sale)
        if (data.warnings?.length) {
          setError(data.warnings.join("; "));
          setTimeout(() => onSuccess(data), 3000);
        } else {
          onSuccess(data);
        }
      } else {
        let errMsg = data.errors?.join("; ") || data.error || data.publish_error || "Failed";
        const rawForLLM = data.publish_error || (Array.isArray(data.errors) ? data.errors.join("\n") : "") || errMsg;
        if (errMsg.includes("Set up") && errMsg.includes("business policies")) {
          errMsg = "You need to set up Business Policies in eBay Seller Hub first.";
        } else if (errMsg.includes("session") || errMsg.includes("expired") || errMsg.includes("Could not fetch")) {
          errMsg = "Your eBay session expired. Go to Profile and reconnect.";
        }
        setError(errMsg);

        // Fire-and-forget: ask Claude to explain this error in plain English.
        // Replaces the raw eBay JSON the user normally sees with a friendly
        // summary + "what to fix" + fault attribution.
        setExplainingError(true);
        ebayAPI
          .explainError({
            raw_error: rawForLLM,
            title: formData?.title || "",
            description: formData?.description || "",
            condition: formData?.condition || "",
            condition_enum: conditionEnum || "",
            category_id: formData?.category_id || "",
            category_name: formData?.category_name || "",
            price: formData?.price ? Number(formData.price) : null,
            sku: formData?.sku || "",
          })
          .then((r) => setErrorExplanation(r.data))
          .catch(() => {})
          .finally(() => setExplainingError(false));

        // In rapid-fire flow, surface the failure so the queue can mark this
        // item as publish_failed (NOT skipped) and offer a Retry Publish later.
        // We delay so the user can read the error before the queue advances.
        if (onFailure && data.retryable) {
          setTimeout(() => {
            onFailure({
              error: errMsg,
              retryable: true,
              verdict_id: verdict.id,
              publish_error: data.publish_error,
            });
          }, 3000);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="ebay-listing-modal">
        <div className="bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex items-center gap-3">
          <Spinner size={24} weight="bold" className="animate-spin" />
          <span className="font-heading font-bold uppercase text-sm">Preparing listing...</span>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="ebay-listing-modal">
        <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-sm w-full">
          <p className="font-body text-sm text-red-600">{error || "Failed to load form"}</p>
          <button onClick={onClose} className="mt-4 w-full p-2 bg-black text-white font-heading font-bold uppercase text-sm">Close</button>
        </div>
      </div>
    );
  }

  const requiredAspects = (formData.aspects || []).filter((a) => a.required);
  const recommendedAspects = (formData.aspects || []).filter((a) => !a.required);

  // eBay standardized sizes: when a Size Type is selected, only show the sizes
  // valid for that type (from taxonomy valueConstraints).
  const sizeTypeSel = (aspects["Size Type"] || "").trim().toLowerCase();
  const withEffectiveValues = (a) => {
    if (a.values_by_type && sizeTypeSel) {
      const key = Object.keys(a.values_by_type).find((k) => k.toLowerCase() === sizeTypeSel);
      if (key) return { ...a, values: a.values_by_type[key] };
    }
    return a;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center pb-[72px] sm:pb-0" data-testid="ebay-listing-modal">
      <div className="bg-white border-t-4 sm:border-4 border-black w-full sm:max-w-lg sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-h-[85vh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-black shrink-0">
          <h2 className="font-heading text-lg font-black uppercase tracking-tight">{editMode ? "Edit Listing" : "List on eBay"}</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowCorrect(true)}
              className="p-1.5 border-2 border-black hover:bg-neutral-50 active:translate-y-[1px] flex items-center gap-1"
              title="Correct this listing with AI"
              data-testid="modal-correct-btn"
            >
              <PencilSimple size={14} weight="bold" />
              <span className="font-heading text-[10px] font-bold uppercase tracking-wide hidden sm:inline">Correct</span>
            </button>
            <button onClick={onClose} className="p-1" data-testid="close-listing-modal">
              <X size={20} weight="bold" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {policiesError && (
            <div
              className="border-2 border-red-500 bg-red-50 p-3 flex items-start gap-2"
              data-testid="policies-error-banner"
            >
              <Warning size={16} weight="bold" className="text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-heading text-[11px] font-bold uppercase tracking-wide text-red-800">
                  eBay session issue
                </div>
                <div className="font-body text-xs text-red-700 mt-0.5">{policiesError}</div>
              </div>
              <a
                href="/profile"
                className="font-heading text-[10px] font-bold uppercase tracking-wide bg-black text-white px-2 py-1 shrink-0"
                data-testid="reconnect-ebay-link"
              >
                Reconnect
              </a>
            </div>
          )}
          {/* Custom Label (SKU) — TOP OF MODAL so OCR mistakes are obvious and fixable.
              Editable on create; read-only on edit (eBay doesn't allow renaming post-publish). */}
          <div className={`border-4 ${!editMode && !customLabel.trim() ? "border-red-500 bg-red-50" : "border-black bg-yellow-50"} p-3`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="font-heading text-xs font-black uppercase tracking-wider flex items-center gap-1" htmlFor="listing-custom-label">
                Custom Label / SKU
                {!editMode && <span className="text-red-600">*</span>}
              </label>
              {labelAutoDetected && !editMode && (
                <span
                  className="font-heading text-[9px] font-bold uppercase tracking-wider bg-[#00E676] text-black border border-black px-1.5 py-0.5"
                  data-testid="label-detected-chip"
                >
                  Detected from sticker
                </span>
              )}
              {editMode && (
                <span className="font-heading text-[9px] font-bold uppercase tracking-wider bg-neutral-200 text-neutral-700 border border-neutral-400 px-1.5 py-0.5">
                  Locked · eBay immutable
                </span>
              )}
            </div>
            {editMode ? (
              <p
                className="p-2 bg-white border-2 border-neutral-300 font-mono text-sm text-neutral-700"
                data-testid="listing-custom-label-readonly"
              >
                {customLabel || <span className="text-neutral-400 italic">(none)</span>}
              </p>
            ) : (
              <input
                id="listing-custom-label"
                type="text"
                value={customLabel}
                onChange={(e) => { setCustomLabel(e.target.value.slice(0, 50)); setLabelAutoDetected(false); }}
                placeholder="Required — e.g. 1001, BIN-A-12, STORE 4 BIN 2 ITEM 568903q7XY"
                maxLength={50}
                required
                aria-required="true"
                aria-invalid={!customLabel.trim() ? "true" : "false"}
                className={`w-full p-2 border-2 ${!customLabel.trim() ? "border-red-500" : "border-black"} font-mono text-sm bg-white`}
                data-testid="listing-custom-label"
              />
            )}
            <p className={`mt-1.5 font-body text-[10px] ${!editMode && !customLabel.trim() ? "text-red-600 font-bold" : "text-neutral-600"}`}>
              {!editMode && !customLabel.trim()
                ? "Required to publish. Auto-fills from inventory sticker photos when detected."
                : "Private to you on eBay. Auto-filled from inventory sticker photos when detected — fix here if the OCR got it wrong."}
            </p>
          </div>

          {/* Title */}
          <Field label="Title">
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              maxLength={80}
              className="w-full p-2 border-2 border-black font-body text-sm"
              data-testid="listing-title"
            />
          </Field>

          {/* Price + Condition row */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price ($)">
              <input
                type="text"
                inputMode="decimal"
                value={formData.price ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                    setFormData({ ...formData, price: v });
                    const p = parseFloat(v) || 0;
                    if (allowOffers && p > 0) {
                      setAutoAccept(String(Math.floor(p * (1 - offerPcts.auto / 100))));
                      setMinOffer(String(Math.floor(p * (1 - offerPcts.min / 100))));
                    }
                  }
                }}
                placeholder="0.00"
                className="w-full p-2 border-2 border-black font-body text-sm"
                data-testid="listing-price"
              />
            </Field>
            <Field label="Condition">
              {validConditions.length > 0 ? (
                <select
                  value={conditionEnum}
                  onChange={(e) => setConditionEnum(e.target.value)}
                  className="w-full p-2 border-2 border-black font-body text-sm bg-white"
                  data-testid="listing-condition"
                >
                  {validConditions.map((c) => (
                    <option key={c.id} value={c.enum || c.id}>
                      {c.description || c.enum || c.id}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="p-2 bg-neutral-50 border-2 border-neutral-200 font-body text-sm">{conditionEnum || formData.condition}</p>
              )}
            </Field>
          </div>

          {/* Best Offer */}
          <div className="border-2 border-neutral-200 p-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="allow-offers-toggle">
              <input
                type="checkbox"
                checked={allowOffers}
                onChange={(e) => setAllowOffers(e.target.checked)}
                className="w-4 h-4 border-2 border-black accent-black"
              />
              <span className="font-heading text-xs font-bold uppercase tracking-wide">Allow Offers</span>
            </label>
            {allowOffers && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Field label="Auto Accept ($)">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={autoAccept}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setAutoAccept(v);
                    }}
                    placeholder="0.00"
                    className="w-full p-2 border-2 border-black font-body text-sm"
                    data-testid="auto-accept-price"
                  />
                </Field>
                <Field label="Minimum Offer ($)">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={minOffer}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setMinOffer(v);
                    }}
                    placeholder="0.00"
                    className="w-full p-2 border-2 border-black font-body text-sm"
                    data-testid="min-offer-price"
                  />
                </Field>
              </div>
            )}
          </div>

          {/* Promote Listing */}
          {campaigns.length > 0 && (
            <Field label="Promote Listing">
              <select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                className="w-full p-2 border-2 border-black font-body text-sm bg-white"
                data-testid="listing-campaign"
              >
                <option value="">Don&apos;t promote</option>
                {campaigns.map((c) => (
                  <option key={c.campaign_id} value={c.campaign_id}>
                    {c.name} ({c.ad_rate}%)
                  </option>
                ))}
              </select>
            </Field>
          )}

          {/* Category (locked in edit mode) */}
          {editMode ? (
            <Field label="Category (locked)">
              <p className="p-2 bg-neutral-100 border-2 border-neutral-300 font-body text-sm text-neutral-500">{formData.category_name || formData.category_id}</p>
            </Field>
          ) : (
            <CategoryPicker
              categoryId={formData.category_id}
              categoryName={formData.category_name}
              onChange={handleCategoryChange}
            />
          )}

          {/* Loading indicator for aspects refresh */}
          {refreshingAspects && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200">
              <Spinner size={14} weight="bold" className="animate-spin text-blue-600" />
              <span className="font-body text-xs text-blue-600">Updating item specifics for new category...</span>
            </div>
          )}

          {/* Required Aspects */}
          {requiredAspects.length > 0 && (
            <div>
              <label className="font-heading text-xs font-bold uppercase tracking-wider text-red-600 flex items-center gap-1">
                <Warning size={14} weight="bold" /> Required Item Specifics
              </label>
              <div className="mt-2 space-y-3">
                {requiredAspects.map((aspect) => (
                  <AspectField
                    key={aspect.name}
                    aspect={withEffectiveValues(aspect)}
                    value={aspects[aspect.name] || ""}
                    onChange={(val) => setAspects({ ...aspects, [aspect.name]: val })}
                    required
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recommended Aspects */}
          {recommendedAspects.length > 0 && (
            <details className="group">
              <summary className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500 cursor-pointer">
                Recommended Item Specifics ({recommendedAspects.length})
              </summary>
              <div className="mt-2 space-y-3">
                {recommendedAspects.map((aspect) => (
                  <AspectField
                    key={aspect.name}
                    aspect={withEffectiveValues(aspect)}
                    value={aspects[aspect.name] || ""}
                    onChange={(val) => setAspects({ ...aspects, [aspect.name]: val })}
                  />
                ))}
              </div>
            </details>
          )}

          {/* Photos — full width, tap to set main, swipe to delete */}
          <Field label={`Photos (${listingPhotos.length})`}>
            {listingPhotos.length > 0 ? (
              <div className="space-y-2 mt-1">
                {listingPhotos.map((path, i) => (
                  <div
                    key={path}
                    className={`relative w-full aspect-[4/3] bg-neutral-100 overflow-hidden border-2 ${
                      i === 0 ? "border-[#002FA7]" : "border-black"
                    }`}
                  >
                    <img src={`/api/images/file/${path}`} alt={`Photo ${i + 1}`} className="w-full h-full object-contain" loading="lazy" />
                    {/* Main badge */}
                    {i === 0 && (
                      <div className="absolute top-2 left-2 bg-[#002FA7] text-white px-2 py-1 flex items-center gap-1">
                        <Star size={12} weight="fill" />
                        <span className="font-heading text-[10px] font-bold uppercase">Main</span>
                      </div>
                    )}
                    {/* Set as main button */}
                    {i > 0 && (
                      <button
                        onClick={() => {
                          const reordered = [...listingPhotos];
                          const [moved] = reordered.splice(i, 1);
                          reordered.unshift(moved);
                          setListingPhotos(reordered);
                        }}
                        className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 flex items-center gap-1 hover:bg-black"
                      >
                        <Star size={12} weight="bold" />
                        <span className="font-heading text-[10px] font-bold uppercase">Set Main</span>
                      </button>
                    )}
                    {/* Delete button */}
                    <button
                      onClick={() => setListingPhotos(listingPhotos.filter((_, j) => j !== i))}
                      className="absolute top-2 right-2 bg-red-600 text-white p-1.5 hover:bg-red-700"
                      title="Remove photo"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                ))}
                {/* Add photo button */}
                <AddPhotoButton onPhotoAdded={(path) => setListingPhotos([...listingPhotos, path])} />
              </div>
            ) : (
              <div className="mt-1 space-y-2">
                <p className="font-body text-xs text-amber-600">No photos — add photos below</p>
                <AddPhotoButton onPhotoAdded={(path) => setListingPhotos([...listingPhotos, path])} />
              </div>
            )}
          </Field>

          {/* Description with HTML preview */}
          <DescriptionEditor
            value={formData.description}
            onChange={(val) => setFormData({ ...formData, description: val })}
          />

          {/* Shipping & Return Policies */}
          {policies && (
            <details className="group">
              <summary className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500 cursor-pointer flex items-center gap-1 py-1">
                <span className="group-open:rotate-90 transition-transform inline-block">&#9654;</span>
                Shipping & Return Policies
              </summary>
              <div className="mt-2 space-y-3">
                {[
                  { key: "fulfillment", label: "Shipping Policy", opts: policies.fulfillment },
                  { key: "payment", label: "Payment Policy", opts: policies.payment },
                  { key: "return", label: "Return Policy", opts: policies.return },
                ].map(({ key, label, opts }) => (
                  <div key={key}>
                    <label className="font-body text-xs font-semibold text-neutral-700">{label}</label>
                    <select
                      value={selectedPolicies[key]}
                      onChange={(e) => setSelectedPolicies({ ...selectedPolicies, [key]: e.target.value })}
                      className="w-full mt-0.5 p-2 border-2 border-black font-body text-sm bg-white"
                      data-testid={`policy-${key}`}
                    >
                      <option value="">Use default</option>
                      {(opts || []).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Inventory Sticker Photos — NOT uploaded to eBay, collapsed at the bottom */}
          {stickerPhotoPaths.length > 0 && (
            <details className="group border-2 border-dashed border-neutral-400 bg-neutral-50" data-testid="sticker-photos-section">
              <summary className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-600 cursor-pointer flex items-center gap-2 p-2">
                <span className="group-open:rotate-90 transition-transform inline-block">&#9654;</span>
                Inventory Sticker ({stickerPhotoPaths.length}) · NOT UPLOADED
              </summary>
              <div className="p-2 pt-0 space-y-2">
                <p className="font-body text-[10px] text-neutral-500">
                  These were detected as inventory stickers and will NOT be listed on eBay. Used only to auto-fill the Custom Label above.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {stickerPhotoPaths.map((path) => (
                    <div
                      key={path}
                      className="relative aspect-square bg-white border-2 border-neutral-400 overflow-hidden"
                      data-testid={`sticker-photo-${path}`}
                    >
                      <img src={`/api/images/file/${path}`} alt="Inventory sticker" className="w-full h-full object-cover opacity-75" loading="lazy" />
                      <div className="absolute inset-x-0 bottom-0 bg-black/80 text-white font-heading text-[8px] font-bold uppercase tracking-wider text-center py-0.5">
                        Label · Not uploaded
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-black space-y-2 shrink-0">
          {error && (
            <div className="border-2 border-red-500 bg-red-50 p-3 space-y-2" data-testid="listing-error">
              {errorExplanation ? (
                <>
                  <div className="flex items-start gap-2">
                    <span
                      className={`shrink-0 text-[10px] font-heading font-bold uppercase tracking-wide px-1.5 py-0.5 border-2 border-black ${
                        errorExplanation.fault === "user"
                          ? "bg-amber-200"
                          : errorExplanation.fault === "ebay"
                          ? "bg-blue-200"
                          : "bg-red-200"
                      }`}
                      data-testid="error-fault-badge"
                    >
                      {errorExplanation.fault === "user"
                        ? "Fix listing"
                        : errorExplanation.fault === "ebay"
                        ? "eBay issue"
                        : "App issue"}
                    </span>
                    <div className="font-body text-sm font-bold text-red-900">
                      {errorExplanation.summary}
                    </div>
                  </div>
                  <div className="font-body text-xs text-neutral-800 pl-1">
                    {errorExplanation.what_to_fix}
                  </div>
                  <details className="font-body text-[10px] text-neutral-500">
                    <summary className="cursor-pointer hover:text-neutral-700">
                      Show raw error{errorExplanation.ebay_error_id ? ` (eBay #${errorExplanation.ebay_error_id})` : ""}
                    </summary>
                    <div className="mt-1 max-h-24 overflow-y-auto break-all bg-white border border-red-200 p-1.5">
                      {error}
                    </div>
                  </details>
                </>
              ) : (
                <>
                  <div className="font-body text-xs text-red-700 max-h-24 overflow-y-auto break-all">
                    {error}
                  </div>
                  {explainingError && (
                    <div className="flex items-center gap-1.5 font-body text-[11px] text-neutral-500" data-testid="error-explaining">
                      <Spinner size={11} weight="bold" className="animate-spin" />
                      Reading eBay&apos;s response…
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={submitting || (!editMode && !customLabel.trim())}
            title={!editMode && !customLabel.trim() ? "Add a Custom Label at the top to publish" : undefined}
            className="w-full p-3 bg-[#002FA7] text-white font-heading font-bold uppercase tracking-wide text-sm border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-listing-btn"
          >
            {submitting ? (
              <><Spinner size={16} weight="bold" className="animate-spin" /> {editMode ? "Updating..." : "Publishing..."}</>
            ) : (
              <>{editMode ? "Update Listing" : "Publish to eBay"} <ArrowRight size={16} weight="bold" /></>
            )}
          </button>
        </div>
      </div>

      {/* Correct this listing (AI chat) */}
      {showCorrect && (
        <CorrectListingModal
          verdict={{ ...verdict, ...(formData || {}), title_keywords: formData?.title || verdict.title_keywords }}
          onClose={() => setShowCorrect(false)}
          onCorrected={async (updated) => {
            if (!updated) return;
            // Verdict was corrected on the backend. The cleanest way to
            // surface the change in the listing modal is to fully re-load the
            // listing form — that regenerates the description with the new
            // condition, re-maps condition_enum, re-prefills aspects, etc.
            // Without this, the user sees the title update but the description
            // (and sometimes the condition dropdown) still reflects the old
            // verdict — the exact bug users reported.
            setRefreshingAspects(true);
            try {
              await loadFormData();
            } finally {
              setRefreshingAspects(false);
            }
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function CategoryPicker({ categoryId, categoryName, onChange }) {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const handleSearch = (q) => {
    setQuery(q);
    clearTimeout(timerRef.current);
    if (q.length < 2) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await ebayAPI.categorySuggest(q);
        setResults(data.suggestions || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 400);
  };

  const handleSelect = (cat) => {
    onChange(cat.id, cat.name);
    setSearching(false);
    setQuery("");
    setResults([]);
  };

  if (!searching) {
    return (
      <Field label="Category">
        <button
          onClick={() => setSearching(true)}
          className="w-full p-2 border-2 border-black font-body text-sm text-left flex items-center justify-between hover:bg-neutral-50 transition-colors"
          data-testid="category-picker"
        >
          <span>{categoryName || `Category ID: ${categoryId}`}</span>
          <MagnifyingGlass size={14} weight="bold" className="text-neutral-400" />
        </button>
      </Field>
    );
  }

  return (
    <Field label="Category">
      <div className="border-2 border-black">
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search categories (e.g. Men's T-Shirts)..."
          className="w-full p-2 font-body text-sm border-b border-neutral-200"
          autoFocus
          data-testid="category-search"
        />
        <div className="max-h-40 overflow-y-auto">
          {loading && (
            <div className="p-2 flex items-center gap-2">
              <Spinner size={12} className="animate-spin" />
              <span className="font-body text-xs text-neutral-500">Searching...</span>
            </div>
          )}
          {results.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleSelect(cat)}
              className="w-full p-2 text-left font-body text-sm hover:bg-neutral-100 border-b border-neutral-100 last:border-0"
            >
              {cat.name} <span className="text-neutral-400 text-xs">({cat.id})</span>
            </button>
          ))}
          {!loading && query.length >= 2 && results.length === 0 && (
            <p className="p-2 font-body text-xs text-neutral-500">No categories found</p>
          )}
        </div>
        <button
          onClick={() => { setSearching(false); setQuery(""); setResults([]); }}
          className="w-full p-1.5 bg-neutral-100 font-body text-xs text-neutral-600 hover:bg-neutral-200"
        >
          Cancel
        </button>
      </div>
    </Field>
  );
}

function DescriptionEditor({ value, onChange }) {
  return (
    <div>
      <label className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500">Description</label>
      <div className="mt-1 border-2 border-black [&_.rsw-editor]:min-h-[150px] [&_.rsw-editor]:max-h-[300px] [&_.rsw-editor]:overflow-y-auto [&_.rsw-editor]:font-body [&_.rsw-editor]:text-sm [&_.rsw-toolbar]:border-b-2 [&_.rsw-toolbar]:border-black [&_.rsw-btn]:border [&_.rsw-btn]:border-neutral-300" data-testid="listing-description">
        <RichEditor value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

function AspectField({ aspect, value, onChange, required }) {
  const hasOptions = aspect.values && aspect.values.length > 0 && aspect.mode === "SELECTION_ONLY";
  const unsupported =
    aspect.enforced && value && aspect.values && aspect.values.length > 0 &&
    !aspect.values.some((v) => v.trim().toLowerCase() === value.trim().toLowerCase());

  return (
    <div>
      <label className="font-body text-xs font-semibold text-neutral-700 flex items-center gap-1">
        {aspect.name}
        {required && <span className="text-red-500">*</span>}
      </label>
      {hasOptions ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-0.5 p-2 border-2 border-black font-body text-sm bg-white"
          data-testid={`aspect-${aspect.name.toLowerCase().replace(/\s/g, "-")}`}
        >
          <option value="">Select...</option>
          {aspect.values.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      ) : (
        <div>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full mt-0.5 p-2 border-2 border-black font-body text-sm"
            placeholder={aspect.values?.[0] ? `e.g. ${aspect.values[0]}` : `Enter ${aspect.name}`}
            list={`dl-${aspect.name}`}
            data-testid={`aspect-${aspect.name.toLowerCase().replace(/\s/g, "-")}`}
          />
          {aspect.values && aspect.values.length > 0 && (
            <datalist id={`dl-${aspect.name}`}>
              {aspect.values.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          )}
        </div>
      )}
      {unsupported && (
        <p
          className="mt-0.5 font-body text-[11px] font-semibold text-amber-700"
          data-testid={`aspect-unsupported-${aspect.name.toLowerCase().replace(/\s/g, "-")}`}
        >
          "{value}" isn't an eBay-standard value — pick from the suggestions. Common
          variants (e.g. "Large") are auto-corrected at publish.
        </p>
      )}
    </div>
  );
}


function AddPhotoButton({ onPhotoAdded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = reader.result;
        const { data } = await api.post("/images/upload", {
          images: [b64],
          verdict_id: "listing-edit",
        });
        const paths = data.paths || data.photos || [];
        if (paths.length > 0) {
          onPhotoAdded(paths[0]);
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full p-3 border-2 border-dashed border-black font-heading text-xs font-bold uppercase flex items-center justify-center gap-2 hover:bg-neutral-50 disabled:opacity-50"
        data-testid="add-listing-photo-btn"
      >
        {uploading ? (
          <><Spinner size={14} className="animate-spin" /> Uploading...</>
        ) : (
          <><Camera size={16} weight="bold" /> Add Photo</>
        )}
      </button>
    </>
  );
}
